import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

const _ = { debounce };

const FILTER_DEBOUNCE_MS = 500;

function DebouncedFilterInput({ value, onChange, placeholder, className, divStyle, hideSearchIcon, focusFindPlayer, maxLength, disabled, 'data-testid': dataTestId }) {
    const [query, setQuery] = useState(value);
    const filterRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // One debounce instance for the lifetime of the input. Building it in the render body instead
    // gives every keystroke its own instance with its own live timer, so typing n characters fires
    // n onChange calls rather than one after the user stops. Callers that turn onChange into a
    // network request would issue a request per character. Reading onChange through a ref keeps
    // this instance stable even when the parent passes a new inline callback on every render.
    const delayedQuery = useMemo(
        () => _.debounce(q => onChangeRef.current(q), FILTER_DEBOUNCE_MS),
        [],
    );

    // Without this the timer outlives the input: remounting to clear it (or switching away
    // mid-debounce) still fires the stale onChange afterwards, re-applying a search the user
    // has already cleared.
    useEffect(() => () => delayedQuery.cancel(), [delayedQuery]);

    const handleChange = e => {
        setQuery(e.target.value);
        delayedQuery(e.target.value);
    };
    const resetFilter = () => {
        delayedQuery.cancel();
        setQuery('');
        onChange('');
    };

    useEffect(() => {
        if (focusFindPlayer) {
            filterRef?.current?.focus();
        }
    }, [focusFindPlayer]);

    return (
        <div className="filter-wrapper" style={divStyle}>
            {!hideSearchIcon && <i className={`uts uts-search`} />}
            <input type="text" placeholder={placeholder} ref={filterRef} value={query} onChange={handleChange} className={className} maxLength={maxLength} disabled={disabled} data-testid={dataTestId && `${dataTestId}.input`} />
            <i className={`uts uts-close  ${query ? 'd-block' : 'd-none'} bg-white pl2`} style={{ paddingRight: '10px' }} onClick={resetFilter} data-testid={dataTestId && `${dataTestId}.clear-btn`} />
        </div>
    );
}

DebouncedFilterInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    divStyle: PropTypes.objectOf(PropTypes.any),
    hideSearchIcon: PropTypes.bool,
    focusFindPlayer: PropTypes.bool,
    maxLength: PropTypes.number,
    disabled: PropTypes.bool,
    'data-testid': PropTypes.string,
};

DebouncedFilterInput.defaultProps = {
    value: '',
    placeholder: '',
    className: '',
    divStyle: {},
    hideSearchIcon: false,
    focusFindPlayer: false,
    maxLength: null,
    disabled: false,
    'data-testid': null,
};

export default DebouncedFilterInput;
